import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { Observable, Subscription } from 'rxjs';
import { SettingsService } from '../../../core/settings/settings.service';
import { Schema } from '../../../core/_models/schema';
import { PasswordFlow } from './password_flow.enum';
import { catchError, publishLast, refCount } from 'rxjs/operators';
import { timer as observableTimer } from 'rxjs/internal/observable/timer';
import { MemberIdentifier } from '@app/core/_models/member-interface';
import { MemberService } from '@app/core/members/member.service';
import { SessionService } from '@app/core/session/session.service';
import { AbstractControlValueAccessor } from '../../../shared/abstract_control_value_accessor.abstract';
import { MsisdnLoginOtpService } from '@app/core/recaptcha/msisdn-login-otp.service';
import { ClientSecret } from '@app/core/_models/client-secret';
import { RecaptchaData, TooLowRecaptchaScoreError } from '@app/core/recaptcha/recaptcha-commons';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { AppFocusableDirective } from '@app/shared/directives/focusable.directive';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';
import { PasswordResendCaptchaComponent } from '@app/shared/components/password-resend-captcha/password-resend-captcha.component';
import { RecaptchaComponent } from '@app/core/recaptcha/components/recaptcha/recaptcha.component';
import { RecaptchaInfoComponent } from '@app/core/recaptcha/components/recaptcha-info/recaptcha-info.component';

const SHOW_INFO_DELAY = 100;

@Component({
  selector: 'app-password-input',
  templateUrl: './password-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PasswordInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PasswordInputComponent), multi: true },
  ],
  imports: [
    NgClass,
    TranslatePipe,
    ReactiveFormsModule,
    AppFocusableDirective,
    InputErrorsComponent,
    PasswordResendCaptchaComponent,
    RecaptchaComponent,
    RecaptchaInfoComponent,
  ],
})
export class PasswordInputComponent extends AbstractControlValueAccessor implements OnInit, AfterViewInit, OnDestroy, Validator {
  public readonly PASSWORD_FLOW = PasswordFlow;

  @Input() public identifier?: MemberIdentifier;
  @Input() public canLogin = true;
  @Input() public formErrors?: { error?: string } = {};
  @Input() public sendOnInit = false;

  public isSendingPassword: boolean;
  public hasSendPasswordSuccess: boolean;
  public triedToSendPassword: boolean;
  public showInitialCaptcha = false;
  public showResendCaptcha = false;

  @ViewChild('input') public passwordInput: ElementRef;

  public settingsObservable = this.settingsService.getSettingsOnce().pipe(publishLast(), refCount());
  public formControl: UntypedFormControl = new UntypedFormControl('');
  public properties: Schema;
  private flow;
  private formSub: Subscription;

  constructor(private cdr: ChangeDetectorRef,
              private loadingBarService: LoadingBarService,
              private settingsService: SettingsService,
              private sessionService: SessionService,
              private memberService: MemberService,
              private msisdnLoginOtpService: MsisdnLoginOtpService) {
    super();
  }

  public ngOnInit(): void {
    this.settingsObservable.subscribe((settings) => {
      this.properties = new Schema(settings.schema);
    });

    this.formSub = this.formControl.valueChanges.subscribe(() => {
      this.onTouched();
      this.onChange(this.formControl.value);
      this.cdr.detectChanges();
    });

    if (this.sendOnInit || !this.canLogin) {
      const flow = this.canLogin ? this.PASSWORD_FLOW.SEND_THROUGH_SMS : this.PASSWORD_FLOW.OWN;
      this.sendPassword(flow);
    }
  }

  public ngAfterViewInit(): void {
    if (this.passwordInput?.nativeElement) { this.passwordInput.nativeElement.focus(); }
  }

  public ngOnDestroy(): void {
    this.formSub.unsubscribe();
  }

  public isPasswordFlow(flow: PasswordFlow): boolean {
    return flow === this.getPasswordFlow();
  }

  public sendPassword(flow: PasswordFlow, isResend = false): void {
    this.flow = flow;
    this.isSendingPassword = this.triedToSendPassword = true;
    this.loadingBarService.start();

    this.selectSendPasswordMethod(flow, isResend)
      .subscribe(
        (response: ClientSecret) => this.onSuccessfulSendPassword(response),
        () => this.onFailureSendPassword(),
      );
  }

  public writeValue(obj: any): void {
    this.formControl.setValue(obj);
  }

  public validate(): ValidationErrors | any {
    return this.formControl.errors;
  }

  public getSendPasswordType(): string {
    if (this.canLogin) { return 'recover'; }
    return 'set';
  }

  public initialCaptchaFinished(data: RecaptchaData): void {
    this.generateCheckboxOTP(data).subscribe(
      (response: ClientSecret) => {
        this.showInitialCaptcha = false;
        this.cdr.detectChanges();
        this.onSuccessfulSendPassword(response);
      },
    );
  }

  public resendCaptchaFinished(data: RecaptchaData): void {
    this.generateCheckboxOTP(data).subscribe(
      () => {
        this.showResendCaptcha = false;
        this.cdr.detectChanges();
      },
    );
  }

  private generateCheckboxOTP(data: RecaptchaData): Observable<unknown> {
    return this.msisdnLoginOtpService.generateOTPWithCheckboxData(this.identifier as MemberIdentifier, data);
  }

  private generateOTP(isResend: boolean): Observable<ClientSecret> {
    return this.msisdnLoginOtpService.generateOTP(this.identifier)
      .pipe(
        catchError(error => {
          if (error instanceof TooLowRecaptchaScoreError) {
            isResend ? this.showResendCaptcha = true : this.showInitialCaptcha = true;
            this.cdr.detectChanges();
          }
          throw error;
        }),
      );
  }

  private selectSendPasswordMethod(flow: PasswordFlow, isResend: boolean): Observable<ClientSecret> {
    return flow !== PasswordFlow.OWN ? this.generateOTP(isResend) : this.sendPasswordRecoveryLink();
  }

  private sendPasswordRecoveryLink(): Observable<ClientSecret> {
    return this.memberService.resetPassword(this.identifier as MemberIdentifier);
  }

  private onSuccessfulSendPassword(response: ClientSecret): void {
    // When OTP is being sent, server returns a special secret for client and i's being sent back with sign in request.
    // If The secret matches, the server will know the user that is signing in is the same user that requested password.
    this.sessionService.saveOtpSecret(response.client_secret);

    const timer = observableTimer(SHOW_INFO_DELAY);
    timer.subscribe(() => {
      this.isSendingPassword = false;
      this.hasSendPasswordSuccess = true;
      this.loadingBarService.complete();
      this.cdr.markForCheck();
    });
  }

  private onFailureSendPassword(): void {
    const timer = observableTimer(SHOW_INFO_DELAY);
    timer.subscribe(() => {
      this.isSendingPassword = false;
      this.hasSendPasswordSuccess = false;
      this.loadingBarService.complete();
      this.cdr.markForCheck();
    });
  }

  private getPasswordFlow(): PasswordFlow {
    if (this.properties.isMsisdnIdentifier()) { return PasswordFlow.SEND_THROUGH_SMS; }
    return PasswordFlow.OWN;
  }
}

